import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
    Card, Table, Button, Space, Tag, message, Modal, Input, Select,
    DatePicker, Row, Col, Statistic, Tooltip, Dropdown, Typography,
} from "antd";
import {
    UploadOutlined, ReloadOutlined, LinkOutlined,
    StopOutlined, UndoOutlined, SearchOutlined,
    EyeOutlined, ExclamationCircleOutlined, FileTextOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { settlementApi } from "../../../api/settlementApi";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import FlightRecordImportModal from "./components/FlightRecordImportModal";
import ManualLinkModal from "./components/ManualLinkModal";
import AircraftMappingDrawer from "./components/AircraftMappingDrawer";
import FlightRecordDetailDrawer from "./components/FlightRecordDetailDrawer";
import { MATCH_STATUS_CONFIG, MATCH_STATUS_OPTIONS } from "./constants";

dayjs.extend(utc);

const { RangePicker } = DatePicker;
const { Text } = Typography;

const fmtDateLocal = (v) => {
    if (!v) return "-";
    const d = dayjs.utc(v).utcOffset(8);
    return d.isValid() ? d.format("YYYY-MM-DD") : v;
};
const fmtTime = (v) => {
    if (!v) return "N/A";
    const d = dayjs.utc(v);
    return d.isValid() ? d.format("MM-DD HH:mm") : v;
};

const FlightRecordLogPage = () => {
    const { hasPermission } = useAuth();
    const canImport = hasPermission("settlement:import");

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
    const [filters, setFilters] = useState({
        matchStatus: [],
        flightNumber: "",
        aircraftMsn: "",
        dateRange: null,
        importBatchId: null,
        hasWarning: false,
    });

    const [importModalVisible, setImportModalVisible] = useState(false);
    const [linkModal, setLinkModal] = useState({ visible: false, record: null });
    const [detailDrawer, setDetailDrawer] = useState({ visible: false, recordId: null });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [statusSummary, setStatusSummary] = useState({});
    const [mappingDrawerVisible, setMappingDrawerVisible] = useState(false);

    const [batchOptions, setBatchOptions] = useState([]);
    const [aircraftOptions, setAircraftOptions] = useState([]);
    const [batchSummary, setBatchSummary] = useState(null);
    const [batchSummaryLoading, setBatchSummaryLoading] = useState(false);

    const filtersRef = useRef(filters);
    filtersRef.current = filters;
    const fetchSeqRef = useRef(0);

    // ── 加载批次列表（用于下拉筛选） ──

    const fetchBatches = useCallback(async () => {
        try {
            const res = await settlementApi.flightRecord.getBatches();
            const list = res.data || res;
            setBatchOptions(
                (Array.isArray(list) ? list : []).map((b) => ({
                    value: b.batch_id,
                    label: `${b.source_file || b.batch_id?.slice(0, 11)} (${b.record_count}条)`,
                    record_count: b.record_count,
                    created_at: b.created_at,
                })),
            );
        } catch (err) {
            console.warn("批次加载失败:", err);
        }
    }, []);

    const fetchAircraftOptions = useCallback(async () => {
        try {
            const res = await settlementApi.flightRecord.getAircraftOptions();
            const list = res.data || res;
            setAircraftOptions(
                (Array.isArray(list) ? list : []).map((msn) => ({ value: msn, label: msn })),
            );
        } catch (err) {
            console.warn("飞机号选项加载失败:", err);
        }
    }, []);

    useEffect(() => {
        fetchBatches();
        fetchAircraftOptions();
    }, [fetchBatches, fetchAircraftOptions]);

    // ── 加载批次汇总（选中批次时触发） ──

    useEffect(() => {
        if (!filters.importBatchId) {
            setBatchSummary(null);
            return;
        }
        let cancelled = false;
        const fetchSummary = async () => {
            setBatchSummaryLoading(true);
            try {
                const res = await settlementApi.flightRecord.getBatchSummary(filters.importBatchId);
                if (!cancelled) setBatchSummary(res.data || res);
            } catch (err) {
                console.warn("批次汇总加载失败:", err);
                if (!cancelled) setBatchSummary(null);
            } finally {
                if (!cancelled) setBatchSummaryLoading(false);
            }
        };
        fetchSummary();
        return () => { cancelled = true; };
    }, [filters.importBatchId]);

    // ── 数据加载 ──

    const fetchData = useCallback(async (page = 1, pageSize = 50, overrideFilters) => {
        const seq = ++fetchSeqRef.current;
        setLoading(true);
        try {
            const f = overrideFilters || filtersRef.current;
            const params = { page, page_size: pageSize };
            if (f.matchStatus?.length) params.match_status = f.matchStatus.join(",");
            if (f.flightNumber) params.flight_number = f.flightNumber;
            if (f.aircraftMsn) params.aircraft_msn = f.aircraftMsn;
            if (f.importBatchId) params.import_batch_id = f.importBatchId;
            if (f.dateRange?.[0]) params.date_from = f.dateRange[0].format("YYYY-MM-DD");
            if (f.dateRange?.[1]) params.date_to = f.dateRange[1].format("YYYY-MM-DD");
            if (f.hasWarning) params.has_warning = true;

            const res = await settlementApi.flightRecord.getList(params);
            if (seq !== fetchSeqRef.current) return;
            const result = res.data || res;
            setData(result.records || []);
            setPagination({ current: result.page, pageSize: result.page_size, total: result.total });
            if (result.summary) setStatusSummary(result.summary);
        } catch (err) {
            if (seq !== fetchSeqRef.current) return;
            message.error(`加载失败: ${err.message}`);
        } finally {
            if (seq === fetchSeqRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTableChange = (pag) => {
        fetchData(pag.current, pag.pageSize);
    };

    const handleSearch = () => {
        fetchData(1, pagination.pageSize);
    };

    const handleFilterChange = (key, value) => {
        const next = { ...filters, [key]: value };
        setFilters(next);
        fetchData(1, pagination.pageSize, next);
    };

    const handleReset = () => {
        const empty = { matchStatus: [], flightNumber: "", aircraftMsn: "", dateRange: null, importBatchId: null, hasWarning: false };
        setFilters(empty);
        fetchData(1, pagination.pageSize, empty);
    };

    // ── 操作 ──

    const [rematchLoading, setRematchLoading] = useState(false);
    const [recheckLoading, setRecheckLoading] = useState(false);

    const handleRecheck = async () => {
        setRecheckLoading(true);
        try {
            const res = await settlementApi.flightRecord.recheckWarnings();
            const s = (res.data || res).summary || {};
            Modal.info({
                title: "警告检查完成",
                width: 460,
                content: (
                    <div>
                        <p>共检查 <b>{s.scanned ?? 0}</b> 条记录，当前有警告 <b style={{ color: "#fa8c16" }}>{s.warning_total ?? 0}</b> 条。</p>
                        <p style={{ marginBottom: 4 }}>其中：</p>
                        <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                            <li>Off-ATD 时间差：{s.off_atd ?? 0}</li>
                            <li>OOOI 日期偏移：{s.oooi ?? 0}</li>
                            <li>疑似重复：{s.duplicate ?? 0}</li>
                        </ul>
                        <p style={{ color: "#999", fontSize: 12 }}>本次新增 {s.newly_added ?? 0} 条，清除失效 {s.cleared ?? 0} 条。</p>
                    </div>
                ),
                okText: "查看有警告记录",
                onOk: () => {
                    const next = { ...filtersRef.current, hasWarning: true };
                    setFilters(next);
                    fetchData(1, pagination.pageSize, next);
                },
            });
        } catch (err) {
            const detail = err.response?.data?.detail;
            message.error(`检查失败: ${typeof detail === "string" ? detail : detail?.message || err.message}`);
        } finally {
            setRecheckLoading(false);
        }
    };

    const handleRematch = () => {
        const eligibleIds = selectedRowKeys.filter((id) => {
            const rec = data.find((r) => r.id === id);
            return rec && (rec.match_status === "pending" || rec.match_status === "unmatched" || rec.match_status === "ambiguous");
        });
        if (!eligibleIds.length) {
            message.warning("请选择待匹配/未匹配/多候选的记录");
            return;
        }
        Modal.confirm({
            title: "确认重新匹配",
            content: `将对选中的 ${eligibleIds.length} 条记录重新执行自动匹配，是否继续？`,
            okText: "确认",
            cancelText: "取消",
            onOk: async () => {
                setRematchLoading(true);
                try {
                    const res = await settlementApi.flightRecord.rematch(eligibleIds);
                    const result = res.data || res;
                    const matched = result?.matched ?? 0;
                    const ambiguous = result?.ambiguous ?? 0;
                    const unmatched = result?.unmatched ?? 0;
                    message.success(`重新匹配完成：已匹配 ${matched}，多候选 ${ambiguous}，未匹配 ${unmatched}`);
                    setSelectedRowKeys([]);
                    fetchData(pagination.current, pagination.pageSize);
                } catch (err) {
                    message.error(`重新匹配失败: ${err.message}`);
                } finally {
                    setRematchLoading(false);
                }
            },
        });
    };

    const handleDelete = (recordIds) => {
        const ids = Array.isArray(recordIds) ? recordIds : [recordIds];
        Modal.confirm({
            title: "确认删除",
            icon: <ExclamationCircleOutlined />,
            content: `将永久删除 ${ids.length} 条记录（已匹配的会自动解除关联），此操作不可恢复，是否继续？`,
            okText: "删除",
            okButtonProps: { danger: true },
            cancelText: "取消",
            onOk: async () => {
                try {
                    const res = await settlementApi.flightRecord.deleteRecords(ids);
                    const result = res.data || res;
                    message.success(`已删除 ${result.deleted} 条记录`);
                    setSelectedRowKeys([]);
                    fetchData(pagination.current, pagination.pageSize);
                } catch (err) {
                    const detail = err.response?.data?.detail;
                    const msg = typeof detail === "string" ? detail : detail?.message || err.message;
                    message.error(`删除失败: ${msg}`);
                }
            },
        });
    };

    const handleExclude = (recordIds) => {
        const ids = Array.isArray(recordIds) ? recordIds : [recordIds];
        Modal.confirm({
            title: "标记为已排除",
            content: `将 ${ids.length} 条记录标记为已排除（试飞/无FOP），是否继续？`,
            okText: "确认排除",
            cancelText: "取消",
            onOk: async () => {
                try {
                    await settlementApi.flightRecord.exclude(ids);
                    message.success(`已标记 ${ids.length} 条为已排除`);
                    setSelectedRowKeys([]);
                    fetchData(pagination.current, pagination.pageSize);
                } catch (err) {
                    message.error(`标记排除失败: ${err.message}`);
                }
            },
        });
    };

    const handleUnexclude = async (recordId) => {
        try {
            await settlementApi.flightRecord.unexclude(recordId);
            message.success("已取消排除");
            fetchData(pagination.current, pagination.pageSize);
        } catch (err) {
            message.error(`取消排除失败: ${err.message}`);
        }
    };

    const handleRevoke = (recordId) => {
        Modal.confirm({
            title: "确认撤销匹配",
            icon: <ExclamationCircleOutlined />,
            content: "撤销后该记录将不再参与降级展示，确定继续？",
            onOk: async () => {
                try {
                    await settlementApi.flightRecord.revoke(recordId);
                    message.success("匹配已撤销");
                    fetchData(pagination.current, pagination.pageSize);
                } catch (err) {
                    message.error(`撤销失败: ${err.message}`);
                }
            },
        });
    };

    const handleCorrectOooiDate = (record) => {
        const detail = record.match_detail || "";
        const offsetMatch = detail.match(/OOOI日期偏移([+-]?\d+)天/);
        const offsetText = offsetMatch ? offsetMatch[1] + " 天" : "检测到的偏移量";
        Modal.confirm({
            title: "确认修正 OOOI 日期",
            icon: <ExclamationCircleOutlined />,
            content: `该记录的 OOOI 时间日期偏移 ${offsetText}，修正后 BH 日拆分将自动重算。是否确认？`,
            okText: "确认修正",
            cancelText: "取消",
            onOk: async () => {
                try {
                    const res = await settlementApi.flightRecord.correctOooiDate(record.id);
                    message.success(res.data?.message || "OOOI 日期已修正");
                    fetchData(pagination.current, pagination.pageSize);
                } catch (err) {
                    message.error(`修正失败: ${err.response?.data?.detail || err.message}`);
                }
            },
        });
    };

    const handleLinkConfirm = async (rec, flightOperationId, force = false) => {
        const isAmbiguous = rec.match_status === "ambiguous";
        const apiCall = isAmbiguous
            ? () => settlementApi.flightRecord.resolveAmbiguous(rec.id, flightOperationId, force)
            : () => settlementApi.flightRecord.manualLink(rec.id, flightOperationId, force);
        try {
            const res = await apiCall();
            const result = res?.data || res;
            if (result?.success === false && result.conflict) {
                Modal.confirm({
                    title: "关联冲突",
                    icon: <ExclamationCircleOutlined />,
                    content: (result.message || "该航班已被占用") + " 确认后将撤销原关联并重新绑定。",
                    okText: "强制替换",
                    cancelText: "取消",
                    okButtonProps: { danger: true },
                    onOk: () => handleLinkConfirm(rec, flightOperationId, true),
                });
                return;
            }
            message.success(force ? "已强制替换关联" : "关联成功");
            setLinkModal({ visible: false, record: null });
            fetchData(pagination.current, pagination.pageSize);
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409 && detail?.conflict) {
                const msg = typeof detail === "string" ? detail : detail?.message || "该航班已被占用";
                Modal.confirm({
                    title: "关联冲突",
                    icon: <ExclamationCircleOutlined />,
                    content: msg + "\n确认后将撤销原关联并重新绑定到当前记录。",
                    okText: "强制替换",
                    cancelText: "取消",
                    okButtonProps: { danger: true },
                    onOk: () => handleLinkConfirm(rec, flightOperationId, true),
                });
            } else {
                const msg = typeof detail === "string" ? detail : detail?.message || err.message;
                message.error(`关联失败: ${msg}`);
            }
        }
    };

    const handleImportSuccess = () => {
        setImportModalVisible(false);
        fetchData(1, pagination.pageSize);
        fetchBatches();
    };

    // ── 行操作菜单 ──

    const getRowActions = (record) => {
        const items = [
            {
                key: "detail",
                label: "查看详情",
                icon: <FileTextOutlined />,
                onClick: () => setDetailDrawer({ visible: true, recordId: record.id }),
            },
        ];

        if (record.match_status === "ambiguous" && canImport) {
            items.push({
                key: "linkFlight",
                label: "选择候选",
                icon: <EyeOutlined />,
                onClick: () => setLinkModal({ visible: true, record }),
            });
        }
        if ((record.match_status === "unmatched" || record.match_status === "pending") && canImport) {
            items.push({
                key: "linkFlight",
                label: "手动关联",
                icon: <LinkOutlined />,
                onClick: () => setLinkModal({ visible: true, record }),
            });
            items.push({
                key: "exclude",
                label: "标记为已排除",
                icon: <StopOutlined />,
                onClick: () => handleExclude([record.id]),
            });
        }
        if (record.match_status === "excluded" && canImport) {
            items.push({
                key: "unexclude",
                label: "取消排除",
                icon: <UndoOutlined />,
                onClick: () => handleUnexclude(record.id),
            });
        }
        if ((record.match_status === "matched" || record.match_status === "manual") && canImport) {
            items.push({
                key: "revoke",
                label: "撤销匹配",
                icon: <UndoOutlined />,
                danger: true,
                onClick: () => handleRevoke(record.id),
            });
        }
        if (record.match_detail && record.match_detail.includes("OOOI日期偏移") && canImport) {
            items.push({
                key: "correctOooi",
                label: "修正 OOOI 日期",
                icon: <ExclamationCircleOutlined style={{ color: "#fa8c16" }} />,
                onClick: () => handleCorrectOooiDate(record),
            });
        }
        if (canImport) {
            items.push({ type: "divider" });
            items.push({
                key: "delete",
                label: "删除",
                danger: true,
                onClick: () => handleDelete([record.id]),
            });
        }
        return items;
    };

    // ── 表格列 ──

    const columns = [
        {
            title: "航班号",
            dataIndex: "flight_number_norm",
            width: 110,
            fixed: "left",
            render: (v, rec) => (
                <Tooltip title={`原始: ${rec.flight_number_raw}`}>
                    <a
                        onClick={() => setDetailDrawer({ visible: true, recordId: rec.id })}
                        style={{ fontWeight: 500 }}
                    >
                        {v || rec.flight_number_raw}
                    </a>
                </Tooltip>
            ),
        },
        {
            title: "日期",
            dataIndex: "flight_date",
            width: 115,
            render: (v) => v || "-",
        },
        {
            title: "航线",
            width: 130,
            render: (_, rec) => `${rec.settlement_departure_airport || "?"} → ${rec.settlement_arrival_airport || "?"}`,
        },
        {
            title: "飞机(原始)",
            dataIndex: "aircraft_msn",
            width: 100,
            render: (v) => v || "-",
        },
        {
            title: "飞机(匹配)",
            dataIndex: "settlement_aircraft_reg",
            width: 100,
            render: (v) => v || "-",
        },
        {
            title: "BH",
            dataIndex: "settlement_bh",
            width: 80,
            align: "right",
            render: (v) => v != null ? v.toFixed(2) : "-",
        },
        {
            title: "FH",
            dataIndex: "settlement_fh",
            width: 80,
            align: "right",
            render: (v) => v != null ? v.toFixed(2) : "-",
        },
        {
            title: "匹配状态",
            dataIndex: "match_status",
            width: 110,
            render: (status, rec) => {
                const cfg = MATCH_STATUS_CONFIG[status] || { color: "default", label: status };
                const suspicious = rec.match_detail?.includes("⚠");
                return (
                    <Space size={4}>
                        <Tag color={cfg.color}>{cfg.label}</Tag>
                        {suspicious && (
                            <Tooltip title="Off 与 ATD 时间差距过大，可能匹配错误">
                                <ExclamationCircleOutlined style={{ color: "#faad14", fontSize: 14 }} />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "匹配航班",
            width: 300,
            render: (_, rec) => {
                const mf = rec.matched_flight;
                if (!mf) {
                    if (rec.match_detail) return <Text type="secondary" style={{ fontSize: 12 }}>{rec.match_detail}</Text>;
                    return <Text type="secondary">-</Text>;
                }
                return (
                    <Tooltip title={`航班ID: ${mf.id}  ATD: ${fmtTime(mf.atd)}  ATA: ${fmtTime(mf.ata)}`}>
                        <span>
                            <Text strong>{mf.flight_number}</Text>
                            {" "}
                            {fmtDateLocal(mf.flight_date)}
                            {" "}
                            {mf.departure_airport} → {mf.arrival_airport}
                            {mf.aircraft_reg && <Text type="secondary"> ({mf.aircraft_reg})</Text>}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "匹配说明",
            dataIndex: "match_detail",
            width: 200,
            ellipsis: true,
            render: (v) => {
                if (!v) return "-";
                const suspicious = v.includes("⚠");
                return (
                    <Text type={suspicious ? "warning" : "secondary"} style={{ fontSize: 12 }}>
                        {v}
                    </Text>
                );
            },
        },
        {
            title: "批次",
            dataIndex: "import_batch_id",
            width: 110,
            ellipsis: true,
            render: (v) => <Text copyable={{ text: v }} style={{ fontSize: 11 }}>{v?.slice(0, 11)}</Text>,
        },
        {
            title: "操作",
            width: 80,
            fixed: "right",
            render: (_, record) => {
                const items = getRowActions(record);
                if (!items.length) return null;
                return (
                    <Dropdown menu={{ items }} trigger={["click"]}>
                        <Button type="link" size="small">操作</Button>
                    </Dropdown>
                );
            },
        },
    ];

    // ── 顶部统计 ──

    const summaryDisplay = useMemo(() => {
        const counts = { total: pagination.total, matched: 0, ambiguous: 0, unmatched: 0, manual: 0, excluded: 0, pending: 0 };
        Object.keys(counts).forEach((key) => {
            if (key !== "total" && statusSummary[key] !== undefined) counts[key] = statusSummary[key];
        });
        return counts;
    }, [pagination.total, statusSummary]);

    // ── 批次汇总统计栏 ──

    const batchStatsBar = useMemo(() => {
        if (!filters.importBatchId || !batchSummary) return null;
        return (
            <Card size="small" style={{ marginBottom: 16, background: "#fafafa" }} loading={batchSummaryLoading}>
                <Row gutter={16} align="middle">
                    <Col>
                        <Text strong style={{ marginRight: 12 }}>批次汇总</Text>
                    </Col>
                    <Col><Statistic title="总数" value={batchSummary.total ?? 0} /></Col>
                    <Col><Statistic title="已匹配" value={batchSummary.matched ?? 0} valueStyle={{ color: "#52c41a" }} /></Col>
                    <Col><Statistic title="手动关联" value={batchSummary.manual ?? 0} valueStyle={{ color: "#1890ff" }} /></Col>
                    <Col><Statistic title="多候选" value={batchSummary.ambiguous ?? 0} valueStyle={{ color: "#faad14" }} /></Col>
                    <Col><Statistic title="未匹配" value={batchSummary.unmatched ?? 0} valueStyle={{ color: "#ff4d4f" }} /></Col>
                    <Col><Statistic title="已排除" value={batchSummary.excluded ?? 0} valueStyle={{ color: "#722ed1" }} /></Col>
                    <Col><Statistic title="待匹配" value={batchSummary.pending ?? 0} /></Col>
                </Row>
            </Card>
        );
    }, [filters.importBatchId, batchSummary, batchSummaryLoading]);

    return (
        <div style={{ padding: 24 }}>
            <Card
                title="飞行记录管理"
                extra={
                    <Space>
                        <Button onClick={() => setMappingDrawerVisible(true)}>
                            飞机号对照
                        </Button>
                        <PermissionGuard permission="settlement:import">
                            <Button
                                icon={<WarningOutlined />}
                                loading={recheckLoading}
                                onClick={handleRecheck}
                            >
                                检查警告
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="settlement:import">
                            <Button type="primary" icon={<UploadOutlined />} onClick={() => setImportModalVisible(true)}>
                                导入飞行记录表
                            </Button>
                        </PermissionGuard>
                    </Space>
                }
            >
                {/* 统计卡片 */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col><Statistic title="总计" value={summaryDisplay.total} /></Col>
                    <Col><Statistic title="已匹配" value={summaryDisplay.matched} valueStyle={{ color: "#52c41a" }} /></Col>
                    <Col><Statistic title="手动关联" value={summaryDisplay.manual} valueStyle={{ color: "#1890ff" }} /></Col>
                    <Col><Statistic title="多候选" value={summaryDisplay.ambiguous} valueStyle={{ color: "#faad14" }} /></Col>
                    <Col><Statistic title="未匹配" value={summaryDisplay.unmatched} valueStyle={{ color: "#ff4d4f" }} /></Col>
                    <Col><Statistic title="已排除" value={summaryDisplay.excluded} valueStyle={{ color: "#722ed1" }} /></Col>
                </Row>

                {/* 筛选栏 */}
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                    <Col>
                        <Select
                            mode="multiple"
                            placeholder="匹配状态（多选）"
                            allowClear
                            maxTagCount="responsive"
                            style={{ minWidth: 180 }}
                            value={filters.matchStatus}
                            onChange={(v) => handleFilterChange("matchStatus", v || [])}
                            options={MATCH_STATUS_OPTIONS}
                        />
                    </Col>
                    <Col>
                        <Input
                            placeholder="航班号"
                            prefix={<SearchOutlined />}
                            style={{ width: 140 }}
                            value={filters.flightNumber}
                            onChange={(e) => setFilters((f) => ({ ...f, flightNumber: e.target.value }))}
                            onPressEnter={handleSearch}
                        />
                    </Col>
                    <Col>
                        <Select
                            placeholder="飞机号"
                            allowClear
                            showSearch
                            style={{ width: 160 }}
                            value={filters.aircraftMsn || undefined}
                            onChange={(v) => handleFilterChange("aircraftMsn", v || "")}
                            options={aircraftOptions}
                        />
                    </Col>
                    <Col>
                        <RangePicker
                            value={filters.dateRange}
                            onChange={(dates) => handleFilterChange("dateRange", dates)}
                        />
                    </Col>
                    <Col>
                        <Select
                            placeholder="导入批次"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            style={{ width: 260 }}
                            value={filters.importBatchId}
                            onChange={(v) => handleFilterChange("importBatchId", v || null)}
                            options={batchOptions}
                        />
                    </Col>
                    <Col>
                        <Button
                            icon={<WarningOutlined />}
                            type={filters.hasWarning ? "primary" : "default"}
                            danger={filters.hasWarning}
                            onClick={() => handleFilterChange("hasWarning", !filters.hasWarning)}
                        >
                            有警告
                        </Button>
                    </Col>
                    <Col>
                        <Button icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
                    </Col>
                    <Col>
                        <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                    </Col>
                </Row>

                {/* 批次汇总统计（仅选中批次时显示） */}
                {batchStatsBar}

                {/* 批量操作 */}
                {selectedRowKeys.length > 0 && canImport && (
                    <Space style={{ marginBottom: 12 }}>
                        <Text type="secondary">已选 {selectedRowKeys.length} 条</Text>
                        <Button size="small" onClick={handleRematch} loading={rematchLoading}>重新匹配</Button>
                        <Button size="small" onClick={() => handleExclude(selectedRowKeys)}>批量排除</Button>
                        <Button size="small" danger onClick={() => handleDelete(selectedRowKeys)}>批量删除</Button>
                    </Space>
                )}

                {/* 数据表格 */}
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条`,
                    }}
                    onChange={handleTableChange}
                    rowSelection={canImport ? {
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                    } : undefined}
                    scroll={{ x: 1400 }}
                    size="small"
                />
            </Card>

            {/* 导入弹窗 */}
            <FlightRecordImportModal
                open={importModalVisible}
                onClose={() => setImportModalVisible(false)}
                onSuccess={handleImportSuccess}
            />

            {/* 关联航班弹窗（候选选择 + 手动关联统一入口） */}
            <ManualLinkModal
                open={linkModal.visible}
                record={linkModal.record}
                onClose={() => setLinkModal({ visible: false, record: null })}
                onConfirm={handleLinkConfirm}
            />

            {/* 飞行记录详情抽屉 */}
            <FlightRecordDetailDrawer
                open={detailDrawer.visible}
                recordId={detailDrawer.recordId}
                onClose={() => setDetailDrawer({ visible: false, recordId: null })}
            />

            {/* 飞机号对照抽屉 */}
            <AircraftMappingDrawer
                open={mappingDrawerVisible}
                onClose={() => setMappingDrawerVisible(false)}
            />
        </div>
    );
};

export default FlightRecordLogPage;
